import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { Button } from 'primereact/button';
import { Field, useFormikContext } from 'formik';
import {
  LegacyInputTextComponent,
  FormikChips,
  FormikCheckbox,
  FormikMultiSelect,
  TextAreaWithValidation,
  DropdownWithValidation,
} from '../../../components/prime-formik';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import FormLanguageSelector from '../../../components/formik-components/FormLanguageSelector';
import { validateEmails } from '../../../i18n/common';
import SuperLinkActivationOptionsForm from '../components/SuperLinkActivationOptionsForm';
import { useSelector } from 'react-redux';
import SuperLinkFormMinimumMatchPercentageInputGroup from '../components/SuperLinkFormMinimumMatchPercentageInputGroup';

const i18n = defineMessages({
  EmailPlaceholder: {
    id: 'placeholder.email',
    defaultMessage: 'Email Address',
  },
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
  sendIndividual: {
    id: 'superlink.send_individual_report_to_respondent',
    defaultMessage: 'Send Individual Report to respondent if distortion is acceptable',
  },
  sendInterestsCC: {
    id: 'superlink.send_cc-interests_report_to_respondent',
    defaultMessage: 'Send Career Counselling - Interests Report to respondent',
  },
  sendPersonalityCC: {
    id: 'superlink.send_cc-personality_report_to_respondent',
    defaultMessage: 'Send Career Counselling - Personality Report to respondent',
  },
  ccInterestsMinimumMatchPercentage: {
    id: 'report.match-percentage.cc-interests-minimum',
    defaultMessage: 'Career Counselling - Interests Report minimum match percentage',
  },
  ccPersonalityMinimumMatchPercentage: {
    id: 'report.match-percentage.cc-personality-minimum',
    defaultMessage: 'Career Counselling - Personality Report minimum match percentage',
  },
  printFriendlyReport: {
    id: 'superlink.lbl_printer_friendly_report',
    defaultMessage: 'Printer friendly report(s)',
  },
});

const NewSuperLinkForm = ({
  handleCancel,
  listJobProfiles,
  searchJob,
  asmtTemp,
  searching,
  reportForDropDown,
  assessmentTemplates,
  isTempLoading,
  searchTemp,
  handleSelectTemplate,
  respondentTypeList,
  accountCanSelectMatchPercentageForCareerCounselingReports,
}) => {
  const { formatMessage } = useIntl();
  const {
    isSubmitting,
    setFieldValue,
    values: { activationType },
  } = useFormikContext();
  const { translatedTags: locales } = useSelector((state) => state.i18n);

  const sendIndividualLabel = formatMessage(i18n.sendIndividual);
  const sendCCPersonalityLabel = formatMessage(i18n.sendPersonalityCC);
  const sendCCInterestsLabel = formatMessage(i18n.sendInterestsCC);
  const printFriendlyReportLabel = formatMessage(i18n.printFriendlyReport);
  const emailPlaceholder = formatMessage(i18n.EmailPlaceholder);

  return (
    <div className="p-grid">
      <div className="p-sm-12 p-md-6">
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <FormattedMessage id="superlink.name" defaultMessage="SuperLink Name" />
            <FormattedMessage id="attrs.name" defaultMessage="Name">
              {(text) => (
                <Field id="superlink-name" name="name" component={LegacyInputTextComponent} placeholder={text} />
              )}
            </FormattedMessage>
          </div>
          <div className="p-col-12" key="selections">
            <div className="p-grid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="assessmentTemplate">
                    <FormattedMessage id="attrs.assessmentTemplate" defaultMessage="Assessment Template" />
                  </label>
                  <Field
                    id="assessmentTemplate"
                    name="assessmentTemplate"
                    component={renderAsyncSelector}
                    processing={isTempLoading}
                    data={assessmentTemplates}
                    filter
                    valueField="id"
                    textField="title"
                    onSearch={searchTemp}
                    setFieldValue={(fieldName, selectedValue) => {
                      handleSelectTemplate(selectedValue);
                      setFieldValue(fieldName, selectedValue);
                      setFieldValue('targetJob', '');
                    }}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
            <div className="p-grid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="superlink-jobProfileId">
                    <FormattedMessage id="attrs.targetJob" defaultMessage="Target Job Profile" />
                  </label>
                  <Field
                    id="job-profile-id"
                    name="targetJob"
                    component={renderAsyncSelector}
                    processing={searching}
                    data={listJobProfiles}
                    filter
                    valueField="id"
                    textField="name"
                    onSearch={(query) => searchJob(query, asmtTemp)}
                    setFieldValue={setFieldValue}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="respondent-type">
                    <FormattedMessage id="superlink.respondentType" defaultMessage="Respondent Type" />
                  </label>
                  <Field
                    id="respondent-type"
                    name="respondentType"
                    component={DropdownWithValidation}
                    options={respondentTypeList}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
            <SuperLinkActivationOptionsForm setFieldValue={setFieldValue} activationType={activationType} />
            {accountCanSelectMatchPercentageForCareerCounselingReports && (
              <SuperLinkFormMinimumMatchPercentageInputGroup />
            )}
            <div className="p-grid">
              <div className="p-col-12">
                <Field
                  id="sendRespondentReport"
                  name="sendRespondentReport"
                  component={FormikCheckbox}
                  labelText={sendIndividualLabel}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
            <div className="p-grid">
              <div className="p-col-12">
                <Field
                  id="sendRespondentCcInterestsReport"
                  name="sendRespondentCcInterestsReport"
                  component={FormikCheckbox}
                  labelText={sendCCInterestsLabel}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
            <div className="p-grid">
              <div className="p-col-12">
                <Field
                  id="sendRespondentCcPersonalityReport"
                  name="sendRespondentCcPersonalityReport"
                  component={FormikCheckbox}
                  labelText={sendCCPersonalityLabel}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
            <div className="p-grid">
              <div className="p-col-12">
                <Field
                  id="printFriendlyReport"
                  name="printFriendlyReport"
                  component={FormikCheckbox}
                  labelText={printFriendlyReportLabel}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
            <br />

            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="notificationEmails">
                  <FormattedMessage
                    id="superlink.notificationEmail"
                    defaultMessage="Send auto-generated report(s) to"
                  />
                  <br />
                  <FormattedMessage
                    id="superlink.emails.hint"
                    defaultMessage="Press ENTER after each provided email"
                    tagName="small"
                  />
                </label>
                <Field
                  id="notificationEmails"
                  name="notificationEmails"
                  component={FormikChips}
                  setFieldValue={setFieldValue}
                  validate={(e) => validateEmails(e, formatMessage)}
                  placeholder={emailPlaceholder}
                  addOnBlur
                />
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="locale">
                  <FormattedMessage id="superlink.reportLanguage" defaultMessage="Language" />
                </label>
                <Field id="locale" name="locale" component={FormLanguageSelector} options={locales} />
              </div>
            </div>

            <div className="p-grid">
              <div className="p-col-12">
                <FormattedMessage id="superlink.autogenerateReports" defaultMessage="AUTO-GENERATE REPORTS">
                  {(text) => (
                    <strong>
                      <h3 className="field-label" htmlFor="reports">
                        {text}
                      </h3>
                    </strong>
                  )}
                </FormattedMessage>
                <Field
                  name="reports"
                  options={reportForDropDown}
                  setFieldValue={setFieldValue}
                  component={FormikMultiSelect}
                  placeholder="..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-col-12 p-md-6" key="info">
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="description">
              <FormattedMessage id="superlink.description" defaultMessage="DESCRIPTION" />
            </label>
            <Field
              className="form-control"
              id="description"
              name="description"
              component={TextAreaWithValidation}
              rows={5}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="notes">
              <FormattedMessage id="attrs.notes" defaultMessage="Notes" />
            </label>
            <Field className="form-control" id="notes" name="notes" component={TextAreaWithValidation} rows={5} />
          </div>
        </div>
      </div>
      <div className="p-col-12">
        <FormattedMessage id="actions.save" defaultMessage="Save">
          {(text) => (
            <Button
              type="submit"
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
              className="btn btn-primary btn-sm float-right"
              disabled={isSubmitting}
              label={text}
            />
          )}
        </FormattedMessage>
        &nbsp;
        <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
          {(text) => (
            <Button
              type="button"
              className="p-button-secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
              label={text}
            />
          )}
        </FormattedMessage>
      </div>
    </div>
  );
};

NewSuperLinkForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  searching: PropTypes.bool.isRequired,
  isTempLoading: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  searchJob: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  searchTemp: PropTypes.func.isRequired,
  handleSelectTemplate: PropTypes.func.isRequired,
  listJobProfiles: PropTypes.arrayOf(PropTypes.any).isRequired,
  reportForDropDown: PropTypes.arrayOf(PropTypes.any).isRequired,
  assessmentTemplates: PropTypes.arrayOf(PropTypes.any).isRequired,
  respondentTypeList: PropTypes.arrayOf(PropTypes.any).isRequired,
  values: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default NewSuperLinkForm;
